/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { catmatAcitretina, catmatIsotretinoina, catmatTalidomida, catmatTretinoina } from 'api/ImpressaoMedicamentos'
import { Button, Checkbox, HFlow, Icon, Modal, ModalBody, ModalFooter, Tag, Text, VFlow } from 'bold-ui'
import { useAlert } from 'components/alert'
import { Box } from 'components/Box'
import { TitleGroup } from 'components/TitleGroup'
import theme from 'config/theme'
import { SexoEnum, TipoReceitaEnum } from 'graphql/types.generated'
import moment from 'moment'
import React, { useEffect, useState } from 'react'
import { useHistory, useRouteMatch } from 'react-router-dom'
import {
  termosOutrosMedicamentosImpressaoPrescricao,
  termosPrescricaoImpressao,
  termosTalidomidaImpressaoPrescricao,
  tipoReceitaRecord,
} from 'types/enums'

import { downloadMedicamentos } from '../impressao/downloadMedicamentos'
import { ImpressaoMedicamentosInput } from '../impressao/ImpressaoMedicamentosInput'
import { MedicamentoFormModel } from '../MedicamentoFormModel'
import { convertMedicamentoToPrintDto } from '../utils/prescricaoConverter'

export interface MedicamentoImpressaoModel extends MedicamentoFormModel {
  alreadyPrinted: boolean
}
export interface ImpressaoPrescricaoModalProps {
  medicamentos: MedicamentoFormModel[]
  atendimentoId: ID
  cidadaoDataNascimento: LocalDate
  cidadaoSexo: SexoEnum
}

export default function ImpressaoPrescricaoModal(props: ImpressaoPrescricaoModalProps) {
  const { medicamentos, atendimentoId, cidadaoDataNascimento, cidadaoSexo } = props
  const [medicamentosToPrint, setMedicamentosToPrint] = useState<MedicamentoImpressaoModel[]>([])
  const [medicamentosSelected, setMedicamentosSelected] = useState<MedicamentoImpressaoModel[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [subtitleTermos, setSubtitleTermos] = useState('')

  const alert = useAlert()
  const history = useHistory()
  const match = useRouteMatch()

  // TODO (Odilon): enviar termos a serem impressos pro serviço de impressão
  const [termosSelected, setTermosSelected] = useState<string[]>([])

  const isTalidomida = medicamentosToPrint.some((med) => {
    return med.principioAtivoCombo?.codigoCatMat === catmatTalidomida
  })

  const isIsotretinoinaTretinoinaAcitretina = medicamentosToPrint.some((med) => {
    return (
      catmatAcitretina.some((element) => {
        return element === med.principioAtivoCombo?.codigoCatMat
      }) ||
      catmatIsotretinoina.some((element) => {
        return element === med.principioAtivoCombo?.codigoCatMat
      }) ||
      catmatTretinoina.some((element) => {
        return element === med.principioAtivoCombo?.codigoCatMat
      })
    )
  })

  const idadeCidadao = moment().diff(cidadaoDataNascimento, 'years')
  const isCidadaoFemMenor55 = cidadaoSexo === SexoEnum.FEMININO && idadeCidadao < 55

  const handleOnChangeMedicamentosSelected = (
    e: React.ChangeEvent<HTMLInputElement>,
    medicamento: MedicamentoImpressaoModel
  ) => {
    if (e.target.checked) {
      setMedicamentosSelected([...medicamentosSelected, medicamento])
    } else {
      setMedicamentosSelected(medicamentosSelected.filter((med) => med.id !== medicamento.id))
    }
  }

  const handleOnChangeTermosSelected = (e: React.ChangeEvent<HTMLInputElement>, termo: string) => {
    if (e.target.checked) {
      setTermosSelected([
        ...termosSelected,
        termosPrescricaoImpressao[termosOutrosMedicamentosImpressaoPrescricao.ANEXO_XV],
      ])
    } else {
      setTermosSelected(termosSelected.filter((termoSelected) => termoSelected !== termo))
    }
  }

  useEffect(() => {
    const subtitleItems: string[] = []

    const meds: MedicamentoImpressaoModel[] = medicamentos.map((med) => {
      const dto: MedicamentoImpressaoModel = { ...med, alreadyPrinted: false }
      if (catmatAcitretina.includes(med.principioAtivoCombo?.codigoCatMat)) {
        subtitleItems.push('Acitretina')
      } else if (catmatIsotretinoina.includes(med.principioAtivoCombo?.codigoCatMat)) {
        subtitleItems.push('Isotretinoina')
      } else if (catmatTretinoina.includes(med.principioAtivoCombo?.codigoCatMat)) {
        subtitleItems.push('Tretinoina')
      }
      return dto
    })

    setSubtitleTermos(subtitleItems.join(' / '))
    setMedicamentosSelected(meds)
    setMedicamentosToPrint(meds)
  }, [medicamentos])

  const handleCloseModal = () => history.push(match.url.replace('/imprimir', ''))

  const handlePrint = () => {
    setIsLoading(true)
    let copyMedicamentosPrint = [...medicamentosToPrint]

    medicamentosSelected.forEach((_, index) => {
      copyMedicamentosPrint[index] = { ...copyMedicamentosPrint[index], alreadyPrinted: true }
    })

    setMedicamentosToPrint(copyMedicamentosPrint)

    const input: ImpressaoMedicamentosInput = {
      atendimentoId: atendimentoId,
      medicamentos: medicamentosSelected.map((item) => convertMedicamentoToPrintDto(item)),
    }

    downloadMedicamentos(input)
      .then(() => {
        setIsLoading(false)
      })
      .catch(() => {
        alert('danger', `Erro ao imprimir prescrição de medicamentos.`)
        setIsLoading(false)
      })
  }

  return (
    <Modal
      open={true}
      onClose={handleCloseModal}
      closeOnBackdropClick={false}
      style={styles.mainContainer}
      depthLevel={3}
      manageOverflow={false}
    >
      <ModalBody>
        <TitleGroup
          title='Imprimir prescrição'
          style={css`
            margin-bottom: 1.5rem;
          `}
        />
        <Text>Selecione os medicamentos que deseja imprimir</Text>
        <VFlow
          vSpacing={0.5}
          style={css`
            margin-top: 0.5rem;
            margin-bottom: 1rem;
          `}
        >
          {medicamentosToPrint.map((medicamento, index) => {
            const principioAtivo = medicamento.registroManual
              ? medicamento.principioAtivoText
              : medicamento.principioAtivoCombo.principioAtivo.nome
            const concentracao = !medicamento.registroManual
              ? medicamento.principioAtivoCombo.medicamento.concentracao
              : !!medicamento.concentracaoText
              ? medicamento.concentracaoText
              : ''
            const tipoReceita = medicamento.registroManual
              ? medicamento.tipoReceita.tipoReceitaEnum
              : medicamento.principioAtivoCombo.principioAtivo.listaMaterial.tipoReceita
            return (
              <Box key={medicamento.id} style={styles.boxMedicamentos} data-testid={`Medicamento${index}`}>
                <HFlow
                  style={css`
                    display: flex;
                    word-break: break-word;
                  `}
                >
                  <Checkbox
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      handleOnChangeMedicamentosSelected(e, medicamento)
                    }
                    defaultChecked
                    data-testid={`Medicamento${index}Checkbox`}
                  />
                  <VFlow vSpacing={0.3}>
                    <HFlow>
                      <Text
                        fontWeight='bold'
                        data-testid={`PrincipioAtivo${index}`}
                      >{`${principioAtivo} ${concentracao}`}</Text>
                      {tipoReceita !== TipoReceitaEnum.COMUM && (
                        <Tag
                          type='info'
                          style={css`
                            align-self: center;
                            max-height: 1.5rem;
                          `}
                        >
                          {tipoReceitaRecord[tipoReceita]}
                        </Tag>
                      )}
                    </HFlow>
                    <Text data-testid={`Posologia${index}`}>{medicamento.posologia}</Text>
                  </VFlow>
                  {medicamento.alreadyPrinted && (
                    <div css={styles.medicamentoImpresso} data-testid={`Impresso${index}`}>
                      <Icon
                        icon='infoCircleOutline'
                        size={1}
                        style={css`
                          margin-right: 0.5rem;
                        `}
                      />
                      Impresso
                    </div>
                  )}
                </HFlow>
              </Box>
            )
          })}
        </VFlow>
        {(isTalidomida || isIsotretinoinaTretinoinaAcitretina) && (
          <React.Fragment>
            <Text
              css={css`
                margin-bottom: 0.5rem;
              `}
            >
              Selecione os termos que deseja imprimir
            </Text>
            <VFlow
              vSpacing={0.5}
              style={css`
                margin-top: 0.5rem;
                margin-bottom: 1rem;
              `}
            >
              {isTalidomida &&
                Object.keys(termosTalidomidaImpressaoPrescricao).map((key, index) => {
                  return (
                    <Box key={index} style={styles.boxMedicamentos}>
                      <HFlow
                        style={css`
                          display: flex;
                          word-break: break-word;
                        `}
                      >
                        <Checkbox
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            handleOnChangeMedicamentosSelected(e, termosTalidomidaImpressaoPrescricao[key])
                          }
                        />
                        <VFlow vSpacing={0.3}>
                          <HFlow>
                            <Text fontWeight='bold'>{termosTalidomidaImpressaoPrescricao[key]}</Text>
                          </HFlow>
                          <Text>Talidomida</Text>
                        </VFlow>
                      </HFlow>
                    </Box>
                  )
                })}
              {isIsotretinoinaTretinoinaAcitretina && (
                <React.Fragment>
                  {isCidadaoFemMenor55 ? (
                    <Box style={styles.boxMedicamentos}>
                      <HFlow
                        style={css`
                          display: flex;
                          word-break: break-word;
                        `}
                      >
                        <Checkbox
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            handleOnChangeTermosSelected(
                              e,
                              termosOutrosMedicamentosImpressaoPrescricao.ANEXO_XV.toString()
                            )
                          }
                        />
                        <VFlow vSpacing={0.3}>
                          <HFlow>
                            <Text fontWeight='bold'>
                              {termosOutrosMedicamentosImpressaoPrescricao.ANEXO_XV.toString()}
                            </Text>
                          </HFlow>
                          <Text>{subtitleTermos}</Text>
                        </VFlow>
                      </HFlow>
                    </Box>
                  ) : (
                    <Box style={styles.boxMedicamentos}>
                      <HFlow
                        style={css`
                          display: flex;
                          word-break: break-word;
                        `}
                      >
                        <Checkbox
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            handleOnChangeTermosSelected(
                              e,
                              termosOutrosMedicamentosImpressaoPrescricao.ANEXO_XVI.toString()
                            )
                          }
                        />
                        <VFlow vSpacing={0.3}>
                          <HFlow>
                            <Text fontWeight='bold'>{termosOutrosMedicamentosImpressaoPrescricao.ANEXO_XVI}</Text>
                          </HFlow>
                          <Text>{subtitleTermos}</Text>
                        </VFlow>
                      </HFlow>
                    </Box>
                  )}
                </React.Fragment>
              )}
            </VFlow>
          </React.Fragment>
        )}
      </ModalBody>
      <ModalFooter>
        <HFlow justifyContent='flex-end'>
          <Button onClick={handleCloseModal} style={styles.footerButton} data-testid='CancelarImprimirButton'>
            Cancelar
          </Button>
          <Button
            kind='primary'
            onClick={handlePrint}
            style={styles.footerButton}
            disabled={medicamentosSelected.length === 0 && termosSelected.length === 0}
            loading={isLoading}
            data-testid='ImprimirPrescricaoButton'
          >
            Imprimir
          </Button>
        </HFlow>
      </ModalFooter>
    </Modal>
  )
}

const styles = {
  mainContainer: css`
    width: 42.5rem;
    height: auto;
  `,
  boxMedicamentos: css`
    border: 1px solid;
    border-radius: 2px;
    border-color: ${theme.pallete.gray.c60};
    padding: 0.75rem 1.25rem;
  `,
  footerButton: css`
    width: 10rem;
    height: 3rem;
  `,
  medicamentoImpresso: css`
    color: ${theme.pallete.primary.main};
    display: flex;
    align-items: center;
    margin-left: auto;
  `,
}
